import React, { useEffect, useState } from "react";
import axios from "axios";
import { telemetryApi } from "../../config/config";

// Shared popup that shows the ECG images a patient's phone uploaded, served from
// web6 (:3002) — the same box that holds the telemetry / device data:
//   GET api/androidapi/getEcgStrips?userid=<6-digit device_id>  → list
//        → { ok, strips: [{ id, uploaded_ms, duplicated_from }] }
//   GET api/androidapi/getEcgStripImage?id=<strip id>           → image bytes
// Controlled by the parent: pass a patient `userid` (the 6-digit device_id) to
// open, null to close. device_id is used — not the Mongo _id — because it's the
// only patient key stable across servers, and it's what the app stores.
export default function EcgStripsModal({ userid, onClose }) {
  const [strips, setStrips] = useState(null); // null = loading, [] = none
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(null);     // src of the image being viewed full-size

  useEffect(() => {
    if (!userid) return;
    let cancelled = false;
    setStrips(null);
    setError("");
    setZoom(null);
    axios
      .get(telemetryApi + "api/androidapi/getEcgStrips?userid=" + encodeURIComponent(userid))
      .then((res) => {
        if (cancelled) return;
        setStrips((res.data && res.data.strips) || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          (e.response && e.response.data && e.response.data.error) ||
            e.message ||
            "Failed to load images"
        );
        setStrips([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userid]);

  if (!userid) return null;

  const fmtWhen = (s) => {
    const t = s.uploaded_ms;
    if (!t) return "";
    try {
      return new Date(Number(t)).toLocaleString();
    } catch (e) {
      return String(t);
    }
  };

  return (
    <div onClick={onClose} style={styles.overlay}>
      <div onClick={(e) => e.stopPropagation()} style={styles.panel}>
        <div style={styles.header}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#24333e" }}>
            Uploaded ECG images{strips ? " (" + strips.length + ")" : ""}
          </div>
          <button onClick={onClose} style={styles.closeBtn} title="Close">
            ✕
          </button>
        </div>

        {strips === null ? (
          <div style={styles.msg}>Loading images…</div>
        ) : error ? (
          <div style={{ ...styles.msg, color: "#e74c3c" }}>{error}</div>
        ) : !strips.length ? (
          <div style={styles.msg}>No images uploaded yet.</div>
        ) : (
          <div style={styles.grid}>
            {strips.map((s) => {
              const src =
                telemetryApi + "api/androidapi/getEcgStripImage?id=" + encodeURIComponent(s.id);
              return (
                <div
                  key={s.id}
                  style={styles.card}
                  onClick={() => setZoom(src)}
                  title="Click to enlarge"
                >
                  <img src={src} alt="ECG upload" style={styles.thumb} />
                  <div style={styles.cap}>
                    {fmtWhen(s) || " "}
                    {s.duplicated_from ? (
                      <span style={styles.dupTag} title="Duplicated strip">
                        copy
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full-size lightbox */}
      {zoom && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setZoom(null);
          }}
          style={styles.lightbox}
        >
          <img
            src={zoom}
            alt="ECG upload full size"
            style={{
              maxWidth: "94vw",
              maxHeight: "94vh",
              borderRadius: 8,
              boxShadow: "0 10px 40px rgba(0,0,0,0.6)",
              background: "#fff",
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    background: "#fff",
    borderRadius: 10,
    padding: "16px 18px",
    width: 720,
    maxWidth: "94vw",
    maxHeight: "86vh",
    overflowY: "auto",
    boxShadow: "0 12px 48px rgba(0,0,0,0.3)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    borderBottom: "1px solid #eee",
    paddingBottom: 8,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#888",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
  },
  msg: { fontSize: 13, color: "#777", padding: "24px 4px", textAlign: "center" },
  grid: { display: "flex", flexWrap: "wrap", gap: 12 },
  card: {
    width: 200,
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
    background: "#f8fafc",
  },
  thumb: { width: "100%", display: "block", background: "#0f172a" },
  cap: { fontSize: 11, color: "#64748b", padding: "4px 6px", display: "flex", alignItems: "center", gap: 6 },
  dupTag: {
    fontSize: 9,
    fontWeight: 700,
    color: "#b45309",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: 4,
    padding: "0 4px",
  },
  lightbox: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "zoom-out",
  },
};
